/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react'
import { Modal } from 'antd'
import styled from '@emotion/styled'
import SportNinjaLogo from '../SportNinjaLogo'
import {
  CompetitionSetupProvider,
  useCompetitionSetup,
} from './context/CompetitionSetupContext'
import StructureStep from './steps/StructureStep'
import DivisionsStep from './steps/DivisionsStep'
import ConferencesStep from './steps/ConferencesStep'
import SingleDivisionStep from './steps/SingleDivisionStep'
import RegistrationStep from './steps/RegistrationStep'
import CloseButton from '../OnboardingWizardModal/components/CloseButton'
import colors from '@sportninja/common/constants/appColors'
import useWindowSize from 'src/hooks/useWindowSize'

interface CompetitionSetupWizardProps {
  isOpen: boolean
  onClose: () => void
  onSkip?: () => void
  onComplete?: () => void
  onRefetch?: () => void
  competitionId: string
  competitionName: string
}

const StyledModal = styled(Modal)<{ $isFullScreen?: boolean }>`
  .ant-modal-content {
    background: ${colors.NEUTRAL_800};
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
    @media (max-width: 768px) {
      overflow: hidden;
    }
  }

  .ant-modal-body {
    padding: 0;
  }

  ${(props) =>
    props.$isFullScreen &&
    `
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    max-width: none !important;
    padding: 0 !important;
    margin: 0 !important;

    .ant-modal-content {
      height: 100vh;
      max-height: 100vh;
      margin: 0;
      border-radius: 0;
      display: flex;
      flex-direction: column;
      width: 100vw;
      background: ${colors.SECONDARY_500};
    }

    .ant-modal-body {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding: 32px 0;
      background: ${colors.SECONDARY_500};
    }
  `}
`

const StepContent = styled.div`
  width: 60vw;
  max-width: 1080px;
  max-height: 1080px;
  height: 100%;
  background: ${colors.SECONDARY_500};
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    overflow: hidden;
  }
`

const TopHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 24px 16px 16px 16px;
  background: ${colors.SECONDARY_500};
  width: 100%;
  @media (max-width: 768px) {
    padding: 16px 0;
  }
`

const TopLogoContainer = styled.div`
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const CloseButtonContainer = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  padding-right: 16px;
  position: absolute;
  right: 0;
  top: 16px;
`

const StepContentBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    min-height: 0;
    overflow-y: auto;
  }
`

/**
 * Modal Content Component - Renders the appropriate step based on current state
 */
const ModalContent: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { state } = useCompetitionSetup()

  const renderStep = () => {
    switch (state.currentStep) {
      case 1:
        return (
          <StepContent>
            <TopHeader>
              <TopLogoContainer>
                <SportNinjaLogo />
              </TopLogoContainer>
              <CloseButtonContainer>
                <CloseButton onClick={onClose} />
              </CloseButtonContainer>
            </TopHeader>
            <StepContentBody>
              <StructureStep />
            </StepContentBody>
          </StepContent>
        )
      case 2:
        // Show ConferencesStep for conference structure, SingleDivisionStep for single structure, DivisionsStep for multiple structure
        return (
          <StepContent>
            <TopHeader>
              <TopLogoContainer>
                <SportNinjaLogo />
              </TopLogoContainer>
              <CloseButtonContainer>
                <CloseButton onClick={onClose} />
              </CloseButtonContainer>
            </TopHeader>
            <StepContentBody>
              {state.structureType === 'conference' ? (
                <ConferencesStep />
              ) : state.structureType === 'single' ? (
                <SingleDivisionStep />
              ) : (
                <DivisionsStep />
              )}
            </StepContentBody>
          </StepContent>
        )
      case 3:
        return (
          <StepContent>
            <TopHeader>
              <TopLogoContainer>
                <SportNinjaLogo />
              </TopLogoContainer>
              <CloseButtonContainer>
                <CloseButton onClick={onClose} />
              </CloseButtonContainer>
            </TopHeader>
            <StepContentBody>
              <RegistrationStep />
            </StepContentBody>
          </StepContent>
        )
      default:
        return null
    }
  }

  return renderStep()
}

/**
 * Competition Setup Wizard Modal Component
 * Multi-step wizard for competition setup with Structure, Divisions, and Registration steps
 */
const CompetitionSetupWizard: React.FC<CompetitionSetupWizardProps> = ({
  isOpen,
  onClose,
  onSkip,
  onComplete,
  onRefetch,
  competitionId,
  competitionName,
}) => {
  return (
    <CompetitionSetupProvider
      onClose={onClose}
      onSkip={onSkip}
      onComplete={onComplete}
      onRefetch={onRefetch}
      competitionId={competitionId}
      competitionName={competitionName}
    >
      <ModalContentWrapper isOpen={isOpen} onClose={onClose} />
    </CompetitionSetupProvider>
  )
}

/**
 * Modal Content Wrapper - Handles dynamic modal sizing
 */
const ModalContentWrapper: React.FC<{
  isOpen: boolean
  onClose: () => void
}> = ({ isOpen, onClose }) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => {
    return width <= 768
  }, [width])

  return (
    <StyledModal
      open={isOpen}
      onCancel={undefined}
      footer={null}
      closable={false}
      centered={false}
      width='100vw'
      $isFullScreen={true}
      wrapClassName='competition-setup-wizard-modal'
      zIndex={10000}
      styles={{
        body: { padding: 0 },
        mask: {
          backgroundColor: colors.SECONDARY_500,
        },
        content: {
          borderRadius: 0,
          overflow: isMobile ? 'hidden' : 'auto',
        },
      }}
    >
      <ModalContent onClose={onClose} />
    </StyledModal>
  )
}

export default CompetitionSetupWizard
